import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { selectPromotion, viewItemList, viewPromotion } from "@WhiteCap/Common/Utillities/CustomTracking";
import useIntersectionObserver from "@WhiteCap/Services/useIntersectionObserver";
import React, { FC, RefObject } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    itemsMessageGridItem?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    wrapper: {
        css: css`
            box-sizing: border-box;
            font-size: 12px;
            font-weight: 400;
            background-color: #f7f8f9;
            border-top: 1px solid #cccccc;
            margin: 10px 10px;
            padding-top: 5px !important;
            padding: 10px;
        `,
    },
    itemsMessageGridItem: {
        gap: 0,
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: 242px;
                    `,
                    css`
                        min-width: 320px;
                    `,
                    css`
                        min-width: 320px;
                    `,
                    css`
                        min-width: 376px;
                    `,
                    css`
                        min-width: 376px;
                    `,
                ])}
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
        `,
    },
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            padding-bottom: 5px !important;
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    searchScoreDataGridItem: {
        width: 12,
        css: css`
            padding: 5px !important;
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    const handleIntersection = () => {
        // Fire GTM event
        viewItemList({
            event: "view_item_list",
            ecommerce: {
                currency: "USD",
                items: [
                    {
                        item_id: props.product.productNumber,
                        item_name: props.product.productTitle,
                        item_brand: props.product.brand?.name ? props.product.brand?.name : "",
                        price: props.product.unitListPrice,
                    },
                ],
            },
        });

        if (props.product.badges && props.product.badges.length > 0) {
            viewPromotion({
                event: "view_promotion",
                ecommerce: {
                    currency: "USD",
                    items: [
                        {
                            item_id: props.product.productNumber,
                            item_name: props.product.productTitle,
                            item_brand: props.product.brand?.name ? props.product.brand?.name : "",
                            price: props.product.unitListPrice,
                            promotion_name: props.product.badges[0].name,
                            promotion_id: props.product.badges[0].id,
                        },
                    ],
                },
            });
        }
    };

    const handlePromotionClick = () => {
        if (props.product.badges && props.product.badges.length > 0) {
            selectPromotion({ 
                event: "select_promotion",
                promotion_name: props.product.badges[0].name,
                promotion_id: props.product.badges[0].id,
                items: [
                        {
                            item_id: props.product.productNumber,
                            item_name: props.product.productTitle,
                            item_brand: props.product.brand?.name ? props.product.brand?.name : "",
                            price: props.product.unitListPrice,
                        },
                    ],
            });
        }
    };

    const productRef = useIntersectionObserver(handleIntersection, { threshold: 0.3 }) as RefObject<HTMLDivElement>;

    return (
        <div ref={productRef} className="product" onClick={handlePromotionClick}>
            <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
                <GridItem {...styles.leftColumnGridItem}>
                    {props.product.isSponsored && (
                        <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                    )}
                    <ProductListProductImage
                        showImage={props.showImage}
                        showCompare={props.showCompare}
                        showImageBadge={props.showImageBadges}
                        showTextBadge={props.showTextBadges}
                    />
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <GridContainer {...styles.rightColumnGridContainer}>
                        <GridItem {...styles.infoGridItem}>
                            <ProductListProductInformation
                                showBrand={props.showBrand}
                                showTitle={props.showTitle}
                                showPartNumbers={props.showPartNumbers}
                                showAvailability={props.showAvailability}
                                showAttributes={props.showAttributes}
                                showImageBadges={props.showImageBadges}
                                maxImageBadges={props.maximumNumberOfImageBadges}
                                showTextBadges={props.showTextBadges}
                                maxTextBadges={props.maximumNumberOfTextBadges}
                            />
                        </GridItem>
                        <GridItem {...styles.actionsGridItem}>
                            <ProductListActions showPrice={props.showPrice} showAddToList={props.showAddToList} />
                        </GridItem>
                        <GridItem {...styles.itemsMessageGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography>{siteMessage("ProductList_LocationPriceUpdate")}</Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.searchScoreDataGridItem}>
                            <ProductSearchScoreData />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </div>
    );
};

export default withProduct(ProductListProductCard);
